/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * The map's header bar.
 *
 * The page the shell replaced injected the whole bar as markup — the
 * tenant's logo and their own menu links. Its colours, height and font
 * have always travelled as CSS custom properties, so a shell without the
 * bar still reserved room for one and showed nothing in it.
 *
 * The bar's content is data now, and this builds it. Nothing here decides
 * how it looks: that is the stylesheet reading those properties —
 * including the logo's height, which is a share of the bar rather
 * than a pixel count and so cannot be set from here.
 */

import { getConfig } from '../lib/config.js';
import { initMenu } from '../lib/dom.js';

/**
 * Build one list of menu links.
 *
 * @param {HTMLElement|null} list - The `ul` to fill.
 * @param {Array<{title: string, url: string, target: string}>} items
 */
function fillMenu(list, items) {
    if (!list) return;
    list.replaceChildren();
    for (const [index, item] of items.entries()) {
        // The entries are `inline-block`, so the newline the server-rendered
        // list had between them rendered as a space and became part of the
        // gap. Building the list without it drew the links four pixels
        // closer together than the page this replaces.
        if (index) list.append(document.createTextNode('\n'));
        const li = document.createElement('li');
        const a = document.createElement('a');
        // Text, not markup: these titles are typed by an editor.
        a.textContent = item.title;
        a.href = item.url;
        if (item.target) a.target = item.target;
        li.append(a);
        list.append(li);
    }
}

/** Put the tenant's logo, or the map's name, in the bar. */
function fillLogo(link, header) {
    if (!link) return;
    // Without a link of its own the logo points at the map it is on, which
    // is what the page did with `request.full_path`.
    link.href = header.logoLinkUrl || window.location.href;
    link.replaceChildren();
    if (header.logoUrl) {
        const img = document.createElement('img');
        img.src = header.logoUrl;
        img.alt = getConfig()?.uiText?.a11y?.mapLogo || '';
        link.append(img);
    } else {
        link.textContent = header.title || '';
    }
}

/**
 * Fill the header bar from the configuration.
 *
 * Does nothing when the map has no bar — `page-setup.js` has already
 * removed the markup by then.
 */
export function init() {
    const header = getConfig()?.header;
    const bar = document.getElementById('header');
    if (!header?.shown || !bar) return;

    fillLogo(bar.querySelector('[data-header="logo-link"]'), header);

    const free = header.freeItems || [];
    const burger = header.burgerItems || [];
    fillMenu(bar.querySelector('[data-header="free-items"]'), free);

    const menu = document.getElementById('menu');
    fillMenu(menu?.querySelector('[data-header="free-items-small"]'), free);
    fillMenu(menu?.querySelector('[data-header="burger-items"]'), burger);

    // The button opens the panel, so it is offered only when the panel has
    // something in it. On a narrow screen that includes the free links,
    // which the wide layout shows in the bar itself.
    const icon = bar.querySelector('[data-header="burger-icon"]');
    if (icon) {
        icon.hidden = !(burger.length || free.length);
        if (!burger.length) icon.classList.add('small-devices');
    }

    bar.hidden = false;
    if (menu) menu.hidden = burger.length === 0 && free.length === 0;

    // The panel opens from the bar's own button. `map-init.js` wires the
    // footer link to the same panel; this one was left unwired, so the
    // button was there and did nothing.
    if (menu && !menu.hidden) {
        initMenu(bar.querySelector('#menu_button'), menu);
    }
}
